// =============================================================================
// pages/Customer360.js — Müşteri 360 (Faz F)
// =============================================================================
// Tek müşterinin tam profili: bilgileri + siparişleri + adresleri + segmentleri
// + drip subscription'ları + NPS + kupon kullanımı + lifecycle log + audit.
// Admin müşteri ile konuşmadan önce buradan tüm context'i alır.
//
// Kullanım: /customer/:id route ile (örn. /customer/123)
// =============================================================================

import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Box, Typography, Grid, Card, CardContent, Stack, Chip, Avatar,
  Tabs, Tab, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  LinearProgress, Alert,
  // Faz E.1: KDV gorunum override icin 3-state toggle
  ToggleButton, ToggleButtonGroup
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import LabelIcon from "@mui/icons-material/Label";
import StarIcon from "@mui/icons-material/Star";
import TimelineIcon from "@mui/icons-material/Timeline";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import HistoryIcon from "@mui/icons-material/History";
import ReceiptIcon from "@mui/icons-material/Receipt";
import toast from "react-hot-toast";
import { getCustomer360, setUserVatPreference } from "../services/api";

const STATUS_COLOR = {
  pending: 'default', paid_pending_approval: 'warning', confirmed: 'info',
  preparing: 'info', shipped: 'primary', delivered: 'success',
  cancelled: 'error', rejected: 'error', refunded: 'default'
};

function formatTL(amount) {
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 })
    .format(parseFloat(amount) || 0);
}
function formatDate(d) {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function Customer360() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  // Faz E.1: KDV override save sirasinda buton disable etmek icin
  const [vatSaving, setVatSaving] = useState(false);

  useEffect(() => {
    setLoading(true);
    getCustomer360(id)
      .then(r => setData(r.data))
      .catch(e => console.error(e))
      .finally(() => setLoading(false));
  }, [id]);

  // ===========================================================================
  // Faz E.1 — KDV gorunum override save
  // ===========================================================================
  // Toggle 3 secenekten birine basilinca cagrilir:
  //   'group'  -> users.shows_vat_inclusive = NULL (grup default'a don)
  //   'inc'    -> 1 (KDV dahil goster)
  //   'exc'    -> 0 (KDV haric goster)
  // ===========================================================================
  const handleVatChange = async (newMode) => {
    if (!newMode || !data?.customer) return;
    const value = newMode === 'group' ? null : (newMode === 'inc' ? 1 : 0);
    setVatSaving(true);
    try {
      await setUserVatPreference(id, value);
      // Lokal state guncelle — ekran refresh olmasin diye
      setData(prev => ({
        ...prev,
        customer: {
          ...prev.customer,
          vat_user_override: value,
          vat_effective: value !== null ? value : (prev.customer.vat_group_default ?? 1)
        }
      }));
      toast.success('KDV görünümü güncellendi');
    } catch (e) {
      toast.error('Hata: ' + (e?.response?.data?.error || e.message));
    } finally {
      setVatSaving(false);
    }
  };

  if (loading) return <Box p={3}><LinearProgress /></Box>;
  if (!data) return <Box p={3}><Alert severity="error">Müşteri bulunamadı</Alert></Box>;

  const { customer, stats, orders, addresses, segments, drips, nps, coupon_usages, recent_activity, lifecycle_log } = data;

  // Lifecycle segment renk
  const lcColor = customer.lifecycle_segment === 'active' ? 'success'
                : customer.lifecycle_segment === 'at_risk' ? 'warning'
                : customer.lifecycle_segment === 'lost' ? 'error'
                : 'default';

  return (
    <Box sx={{ p: 2 }}>
      {/* HEADER */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={3}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar sx={{ bgcolor: '#1565C0', width: 60, height: 60, fontSize: 24, fontWeight: 700 }}>
            {(customer.name || '?').slice(0, 1).toUpperCase()}
          </Avatar>
          <Box>
            <Typography variant="h5" fontWeight={700}>{customer.name || '(isimsiz)'}</Typography>
            <Stack direction="row" spacing={1} mt={0.5} alignItems="center">
              <Chip
                icon={<LabelIcon />}
                label={customer.lifecycle_segment || 'new'}
                color={lcColor}
                size="small"
              />
              {customer.role === 'admin' && <Chip label="ADMIN" color="secondary" size="small" />}
              <Typography variant="caption" color="text.secondary">
                Üyelik: {formatDate(customer.created_at)}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={2} mt={1}>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <EmailIcon fontSize="small" color="action" />
                <Typography variant="body2">{customer.email}</Typography>
              </Stack>
              {customer.phone && (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <PhoneIcon fontSize="small" color="action" />
                  <Typography variant="body2">{customer.phone}</Typography>
                </Stack>
              )}
            </Stack>
          </Box>
        </Stack>
      </Stack>

      {/* =====================================================================
           B2B AYARLARI BARI — Faz E.1 KDV gorunum override
           ---------------------------------------------------------------------
           3-state ToggleButtonGroup ile admin musteri icin KDV gosterimini
           degistirebilir. Default 'Grup'tan miras al', talep gelirse override.

           current mode mapping:
             vat_user_override === null  -> 'group' (grup default'u izle)
             vat_user_override === 1     -> 'inc'   (KDV dahil)
             vat_user_override === 0     -> 'exc'   (KDV haric)
           ===================================================================== */}
      {(() => {
        const currentMode =
          customer.vat_user_override === null || customer.vat_user_override === undefined
            ? 'group'
            : (customer.vat_user_override ? 'inc' : 'exc');
        const groupDefaultLabel = customer.group_name
          ? `${customer.group_name} → ${customer.vat_group_default ? 'KDV Dahil' : (customer.vat_group_default === 0 ? 'KDV Hariç' : 'tanımsız')}`
          : 'grup atanmamış → KDV Dahil (varsayılan)';
        const effectiveLabel = customer.vat_effective ? 'KDV DAHİL' : 'KDV HARİÇ';
        return (
          <Paper sx={{ p: 2, mb: 2, borderLeft: '4px solid #1565C0' }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between">
              <Stack direction="row" spacing={1.5} alignItems="center">
                <ReceiptIcon sx={{ color: '#1565C0' }} />
                <Box>
                  <Typography variant="subtitle2" fontWeight={700}>Sepet/Sayfa KDV Gösterimi</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Şu an: <strong>{effectiveLabel}</strong>
                    {currentMode === 'group' && <> · grup default'undan miras ({groupDefaultLabel})</>}
                    {currentMode !== 'group' && <> · admin override aktif</>}
                  </Typography>
                </Box>
              </Stack>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={currentMode}
                onChange={(_, v) => handleVatChange(v)}
                disabled={vatSaving}
              >
                <ToggleButton value="group">Grup default</ToggleButton>
                <ToggleButton value="inc">KDV Dahil</ToggleButton>
                <ToggleButton value="exc">KDV Hariç</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Paper>
        );
      })()}

      {/* STAT KARTLARI */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} md={3}>
          <Card sx={{ borderTop: '3px solid #1565C0' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <ShoppingCartIcon sx={{ color: '#1565C0', fontSize: 28 }} />
                <Box>
                  <Typography variant="h4" fontWeight={800}>{stats.total_orders || 0}</Typography>
                  <Typography variant="caption" color="text.secondary">Toplam Sipariş</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ borderTop: '3px solid #10b981' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <AttachMoneyIcon sx={{ color: '#10b981', fontSize: 28 }} />
                <Box>
                  <Typography variant="h5" fontWeight={800}>{formatTL(stats.total_spent)}</Typography>
                  <Typography variant="caption" color="text.secondary">Toplam Harcama</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ borderTop: '3px solid #E67E22' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <AttachMoneyIcon sx={{ color: '#E67E22', fontSize: 28 }} />
                <Box>
                  <Typography variant="h5" fontWeight={800}>{formatTL(stats.avg_order_value)}</Typography>
                  <Typography variant="caption" color="text.secondary">AOV (Ortalama)</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ borderTop: '3px solid #7c3aed' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <HistoryIcon sx={{ color: '#7c3aed', fontSize: 28 }} />
                <Box>
                  <Typography variant="body2" fontWeight={700}>{formatDate(stats.last_order_at)}</Typography>
                  <Typography variant="caption" color="text.secondary">Son Sipariş</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* TABS */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab label={`Siparişler (${orders.length})`} />
          <Tab label={`Segmentler (${segments.length})`} />
          <Tab label={`Drip Kampanyalar (${drips.length})`} />
          <Tab label={`NPS & Kuponlar`} />
          <Tab label={`Aktivite & Lifecycle`} />
          <Tab label={`Adresler (${addresses.length})`} />
        </Tabs>
      </Paper>

      {/* TAB 0: Siparişler */}
      {tab === 0 && (
        <Card>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Tutar</TableCell>
                  <TableCell>Statü</TableCell>
                  <TableCell>Ödeme</TableCell>
                  <TableCell>Kargo</TableCell>
                  <TableCell>BH Fatura</TableCell>
                  <TableCell>Tarih</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.map(o => (
                  <TableRow key={o.id} hover>
                    <TableCell><Link to={`/orders?id=${o.id}`}>#{o.id}</Link></TableCell>
                    <TableCell><strong>{formatTL(o.total_price)}</strong></TableCell>
                    <TableCell><Chip label={o.status} color={STATUS_COLOR[o.status] || 'default'} size="small" /></TableCell>
                    <TableCell>{o.payment_status} ({o.payment_method})</TableCell>
                    <TableCell>{o.tracking_no ? `${o.kargo_carrier || ''} ${o.tracking_no}` : '-'}</TableCell>
                    <TableCell>{o.bh_invoice_status || '-'}</TableCell>
                    <TableCell>{formatDate(o.created_at)}</TableCell>
                  </TableRow>
                ))}
                {orders.length === 0 && (
                  <TableRow><TableCell colSpan={7} align="center"><em>Henüz sipariş yok</em></TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* TAB 1: Segmentler */}
      {tab === 1 && (
        <Grid container spacing={2}>
          {segments.map(s => (
            <Grid item xs={12} md={6} key={s.code}>
              <Card>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="h6" fontWeight={700}>{s.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{s.code}</Typography>
                    </Box>
                    <Box textAlign="right">
                      <Chip label={s.source} size="small" color={s.source === 'auto' ? 'default' : 'primary'} />
                      {s.confidence && (
                        <Typography variant="caption" display="block" mt={0.5}>
                          Güven: {Math.round(s.confidence * 100)}%
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
          {segments.length === 0 && (
            <Grid item xs={12}><Alert severity="info">Bu müşteri henüz hiçbir segmente eklenmemiş.</Alert></Grid>
          )}
        </Grid>
      )}

      {/* TAB 2: Drip Kampanyalar */}
      {tab === 2 && (
        <Card>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Kampanya</TableCell>
                  <TableCell>Mevcut Adım</TableCell>
                  <TableCell>Sonraki Adım</TableCell>
                  <TableCell>Statü</TableCell>
                  <TableCell>Kayıt</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {drips.map(d => (
                  <TableRow key={d.id}>
                    <TableCell><strong>{d.campaign_name}</strong></TableCell>
                    <TableCell>{d.current_step}</TableCell>
                    <TableCell>{d.next_step_at ? formatDate(d.next_step_at) : '-'}</TableCell>
                    <TableCell><Chip label={d.status} size="small" color={d.status === 'active' ? 'primary' : d.status === 'completed' ? 'success' : 'default'} /></TableCell>
                    <TableCell>{formatDate(d.enrolled_at)}</TableCell>
                  </TableRow>
                ))}
                {drips.length === 0 && (
                  <TableRow><TableCell colSpan={5} align="center"><em>Aktif drip kampanya yok</em></TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* TAB 3: NPS & Kuponlar */}
      {tab === 3 && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                  <StarIcon color="warning" />
                  <Typography variant="h6" fontWeight={700}>NPS Yanıtları ({nps.length})</Typography>
                </Stack>
                {nps.length === 0 ? (
                  <Typography color="text.secondary" variant="body2">Henüz NPS yanıtı yok</Typography>
                ) : nps.map((n, i) => (
                  <Box key={i} sx={{ p: 1.5, mb: 1, border: '1px solid #e2e8f0', borderRadius: 1 }}>
                    <Stack direction="row" justifyContent="space-between">
                      <Chip label={`Skor: ${n.score}/10`} size="small"
                        color={n.category === 'promoter' ? 'success' : n.category === 'detractor' ? 'error' : 'warning'} />
                      <Typography variant="caption">{formatDate(n.responded_at)}</Typography>
                    </Stack>
                    {n.feedback && <Typography variant="body2" mt={1} sx={{ fontStyle: 'italic' }}>"{n.feedback}"</Typography>}
                    <Typography variant="caption" color="text.secondary">Sipariş #{n.order_id}</Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                  <LocalOfferIcon color="primary" />
                  <Typography variant="h6" fontWeight={700}>Kupon Kullanımları ({coupon_usages.length})</Typography>
                </Stack>
                {coupon_usages.length === 0 ? (
                  <Typography color="text.secondary" variant="body2">Henüz kupon kullanımı yok</Typography>
                ) : coupon_usages.map((c, i) => (
                  <Box key={i} sx={{ p: 1.5, mb: 1, border: '1px solid #e2e8f0', borderRadius: 1 }}>
                    <Stack direction="row" justifyContent="space-between">
                      <Chip label={c.code} color="primary" size="small" />
                      <Typography variant="caption">{formatDate(c.used_at)}</Typography>
                    </Stack>
                    <Typography variant="body2" mt={0.5}>
                      İndirim: <strong>{formatTL(c.discount_applied)}</strong>
                      {c.order_id && ` — Sipariş #${c.order_id}`}
                    </Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* TAB 4: Aktivite & Lifecycle */}
      {tab === 4 && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                  <TimelineIcon />
                  <Typography variant="h6" fontWeight={700}>Lifecycle Akışı</Typography>
                </Stack>
                {lifecycle_log.length === 0 ? (
                  <Typography color="text.secondary" variant="body2">Henüz aktivite yok</Typography>
                ) : lifecycle_log.slice(0, 15).map((l, i) => (
                  <Box key={i} sx={{ borderLeft: '2px solid #1565C0', pl: 1.5, py: 0.5, mb: 1 }}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" fontWeight={600}>{l.template || l.stage}</Typography>
                      <Chip label={l.result || 'sent'} size="small" color={l.result === 'sent' ? 'success' : 'error'} />
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      {l.channel || 'email'} · {formatDate(l.sent_at)}
                    </Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                  <HistoryIcon />
                  <Typography variant="h6" fontWeight={700}>Son Aktiviteler</Typography>
                </Stack>
                {recent_activity.slice(0, 15).map((a, i) => (
                  <Box key={i} sx={{ pb: 1, mb: 1, borderBottom: '1px solid #f1f5f9' }}>
                    <Typography variant="body2"><strong>{a.action}</strong></Typography>
                    <Typography variant="caption" color="text.secondary">
                      {a.resource_type} #{a.resource_id} · {formatDate(a.created_at)}
                    </Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* TAB 5: Adresler */}
      {tab === 5 && (
        <Grid container spacing={2}>
          {addresses.map(a => (
            <Grid item xs={12} md={6} key={a.id}>
              <Card>
                <CardContent>
                  <Stack direction="row" alignItems="flex-start" spacing={1}>
                    <LocationOnIcon color="action" />
                    <Box flex={1}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" fontWeight={600}>{a.label || 'Adres'}</Typography>
                        {a.is_default ? <Chip label="Varsayılan" size="small" color="primary" /> : null}
                      </Stack>
                      <Typography variant="body2" mt={1}>{a.address_text}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {a.district} / {a.city}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
          {addresses.length === 0 && (
            <Grid item xs={12}><Alert severity="info">Bu müşterinin kayıtlı adresi yok.</Alert></Grid>
          )}
        </Grid>
      )}
    </Box>
  );
}
